import React, { useContext, useState } from 'react'
import { Modal, NotificationManagerContext, Spacer, View } from 'oio-react'
import { Route, Switch, useHistory, useRouteMatch } from 'react-router-dom'
import uiConstants from 'config/constants/ui'
import TicketEditor from 'src/apps/TicketEditor'
import TicketEditorSummaryBlock from 'src/apps/TicketEditor/components/TicketEditorSummaryBlock'
import FormBuilder from 'src/components/Form/Builder'
import ProtoHeadingBlock from 'src/components/Settings/ProtoHeadingBlock'
import ProtoReturnBar from 'src/components/Settings/ProtoReturnBar'
import RegistrationSummaryBlock from 'src/components/Initiative/Settings/RegistrationSummaryBlock'
import { initiativeSettingsUrl } from 'src/components/utils/urls'
import { InitiativeHierarchyContext } from 'src/libraries/contexts/InitiativeHierarchy'
import apiFetch from 'src/libraries/services/apiFetch'

const InitiativeRegistrationSettings = () => {
   const [modalIsOpen, setModalIsOpen] = useState(true)
   const history = useHistory()
   const match = useRouteMatch()
   const { initiative, refetchInitiative } = useContext(InitiativeHierarchyContext)
   const { showNotification } = useContext(NotificationManagerContext)

   const handleRegistrationFormChange = async (formElements) => {
      try {
         const response = await apiFetch(`/initiatives/${initiative.id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
               'apps.registration_form.elements': formElements
            })
         })

         if (response.ok) {
            refetchInitiative()
            showNotification({
               message: `You have successfully updated this ${initiative.type.nameSingular}`,
               title: 'Success!',
               type: 'success'
            })
         } else {
            const result = await response.json()
            showNotification({
               message: result?.message || 'An unexpected error occurred',
               title: 'Error!',
               type: 'error'
            })
         }
      } catch (error) {
         showNotification({
            message: `There was an error while updating this ${initiative.type.nameSingular}`,
            title: 'Error!',
            type: 'error'
         })
      }
   }

   return (
      <View>
         <ProtoReturnBar
            display="block[a-c] none[d-f]"
            returnLinkName={`${initiative.type.nameSingular} Settings`}
            returnLinkTo={initiativeSettingsUrl()}
         />
         <View display="flex" justifyContent="center" width="100%">
            <View
               maxWidth="100%[a-c] 660px[d-f]"
               padding="18px[a] 24px[b] 30px[c] 48px[d-f]">
               <View width="100%">
                  <View
                     float="left"
                     width="100%"
                     marginBottom="24px"
                     display="flex">
                     <ProtoHeadingBlock
                        contentMaxWidth="100%"
                        title={`${initiative.type.nameSingular} Registration & Tickets`}
                        description={`By default, people are able to attend ${initiative.type.namePlural} in one click. However, in some cases, you may want to add admission requirements for people to attend this ${initiative.type.nameSingular}.`}
                     />
                  </View>
                  <RegistrationSummaryBlock editUrl={`${match.url}/form`} />
                  <Spacer size="3" />
                  <TicketEditorSummaryBlock editUrl={`${match.url}/tickets`} />
               </View>
            </View>
         </View>
         <Switch>
            <Route
               path={`${match.url}/form`}
               render={() => (
                  <Modal
                     borderRadius="6px"
                     overlayBackgroundColor="rgba(20,20,20,0.9)"
                     width="100%[a-b] 90%[c] 960px[d-f]"
                     height="100%[a-b] 95%[c-f]"
                     onCloseComplete={() => {
                        history.push(match.url)
                        setModalIsOpen(true)
                     }}
                     open={modalIsOpen}
                     zIndex={uiConstants.zIndexes.tmpSettingsModal}>
                     <FormBuilder
                        formBuilderTitle="Registration Form Builder"
                        formBuilderSubtitle={`This app will help you build out a registration form that users must fill out to ${initiative.joinVerb} the ${initiative.type.nameSingular} <b>${initiative.name}</b>.`}
                        formSubtitle="Registration Form"
                        formTitle={initiative.name}
                        onChange={handleRegistrationFormChange}
                        // eslint-disable-next-line camelcase
                        elements={initiative?.apps?.registration_form?.elements}
                        onCloseButtonClick={() => setModalIsOpen(false)}
                     />
                  </Modal>
               )}
            />
            <Route
               path={`${match.url}/tickets`}
               render={() => (
                  <Modal
                     borderRadius="6px"
                     overlayBackgroundColor="rgba(20,20,20,0.9)"
                     width="100%[a-b] 90%[c] 960px[d] 1100px[e-f]"
                     height="100%[a-b] 85%[c-f]"
                     onCloseComplete={() => {
                        history.push(match.url)
                        setModalIsOpen(true)
                     }}
                     onCloseTrigger={() => setModalIsOpen(false)}
                     open={modalIsOpen}
                     zIndex={uiConstants.zIndexes.tmpSettingsModal}>
                     <TicketEditor
                        onCloseButtonClick={() => setModalIsOpen(false)}
                     />
                  </Modal>
               )}
            />
         </Switch>
      </View>
   )
}

export default InitiativeRegistrationSettings
